import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { formatCurrencyINR } from '../../../utils/currency';
import { 
  generateId, 
  addTimestamps, 
  validateExpense,
  EXPENSE_SCHEMA 
} from '../../../schemas/dataSchemas';
import { useSubscription } from '../../../contexts/SubscriptionContext';
import { useDemo } from '../../../contexts/DemoContext';
import { useUserData } from '../../../hooks/useUserData';

const ExpenseModal = ({ isOpen, onClose, onSave, editingExpense = null }) => {
  const { canWrite } = useSubscription();
  const { isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const { userData } = useUserData();
  
  // Calculate max end year based on projection horizon
  const currentYear = new Date().getFullYear();
  const startYear = userData?.setup?.startYear || currentYear;
  const projectionHorizon = userData?.setup?.projectionHorizon || 20;
  const maxEndYear = startYear + projectionHorizon;
  const [formData, setFormData] = useState({
    description: editingExpense?.description || '',
    amount: editingExpense?.amount || '',
    category: editingExpense?.category || 'personal',
    frequency: editingExpense?.frequency || 'monthly',
    startYear: editingExpense?.startYear || new Date().getFullYear(),
    endYear: editingExpense?.endYear || new Date().getFullYear() + 25,
    inflationRate: editingExpense?.inflationRate || 3.5,
    probability: editingExpense?.probability || 100,
    isOneTime: editingExpense?.isOneTime || false,
    isEssential: editingExpense?.isEssential !== undefined ? editingExpense.isEssential : true,
    priority: editingExpense?.priority || 'medium',
    notes: editingExpense?.notes || ''
  });

  const [errors, setErrors] = useState({});

  // Update form data when editingExpense changes
  useEffect(() => {
    if (editingExpense) {
      setFormData({
        description: editingExpense.description || '',
        amount: editingExpense.amount || '',
        category: editingExpense.category || 'personal',
        frequency: editingExpense.frequency || 'monthly',
        startYear: editingExpense.startYear || new Date().getFullYear(),
        endYear: editingExpense.endYear || new Date().getFullYear() + 25,
        inflationRate: editingExpense.inflationRate || 3.5,
        probability: editingExpense.probability || 100,
        isOneTime: editingExpense.isOneTime || false,
        isEssential: editingExpense.isEssential !== undefined ? editingExpense.isEssential : true,
        priority: editingExpense.priority || 'medium',
        notes: editingExpense.notes || ''
      });
    } else {
      // Reset form when not editing
      setFormData({
        description: '',
        amount: '',
        category: 'personal',
        frequency: 'monthly',
        startYear: new Date().getFullYear(),
        endYear: new Date().getFullYear() + 25,
        inflationRate: 3.5,
        probability: 100,
        isOneTime: false,
        isEssential: true,
        priority: 'medium',
        notes: ''
      });
    }
    setErrors({});
  }, [editingExpense]);

  const categoryOptions = [
    { value: 'medical', label: 'Medical & Healthcare' },
    { value: 'travel', label: 'Travel & Leisure' },
    { value: 'personal', label: 'Personal & Lifestyle' },
    { value: 'home', label: 'Home & Utilities' },
    { value: 'food', label: 'Food & Dining' },
    { value: 'transport', label: 'Transportation' },
    { value: 'kids', label: 'Children & Education' },
    { value: 'parents', label: 'Parents & Elderly Care' },
    { value: 'insurance', label: 'Insurance' },
    { value: 'entertainment', label: 'Entertainment' },
    { value: 'other', label: 'Other' }
  ];

  const frequencyOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
    { value: 'annually', label: 'Annually' },
    { value: 'one-time', label: 'One-time' }
  ];

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'critical', label: 'Critical' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check required fields first with user-friendly messages
    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Expense description is required';
    }
    
    if (!formData.amount || formData.amount === '' || parseFloat(formData.amount) <= 0) {
      newErrors.amount = 'Amount is required and must be greater than 0';
    }
    
    if (!formData.category || formData.category === '') {
      newErrors.category = 'Category is required';
    }
    
    if (!formData.frequency || formData.frequency === '') {
      newErrors.frequency = 'Frequency is required';
    }
    
    const startYearValue = parseInt(formData.startYear);
    const endYearValue = parseInt(formData.endYear);
    const minYear = currentYear - 1;
    
    if (!formData.startYear || isNaN(startYearValue) || startYearValue < minYear || startYearValue > maxEndYear) {
      newErrors.startYear = `Start year is required and must be between ${minYear} and ${maxEndYear}`;
    }
    
    if (!formData.endYear || isNaN(endYearValue) || endYearValue < minYear || endYearValue > maxEndYear) {
      newErrors.endYear = `End year is required and must be between ${minYear} and ${maxEndYear}`;
    }
    
    if (startYearValue && endYearValue && startYearValue > endYearValue) {
      if (!newErrors.startYear) newErrors.startYear = 'Start year must be before end year';
      if (!newErrors.endYear) newErrors.endYear = 'End year must be after start year';
    }
    
    // Validate amount is a valid number
    const amount = parseFloat(formData.amount);
    if (formData.amount && (isNaN(amount) || amount <= 0)) {
      newErrors.amount = 'Amount must be a valid positive number';
    }
    
    // Validate optional fields if provided
    if (formData.inflationRate !== undefined && formData.inflationRate !== '') {
      const inflationRate = parseFloat(formData.inflationRate);
      if (isNaN(inflationRate) || inflationRate < 0 || inflationRate > 20) {
        newErrors.inflationRate = 'Inflation rate must be between 0% and 20%';
      }
    }
    
    if (formData.probability !== undefined && formData.probability !== '') {
      const probability = parseFloat(formData.probability);
      if (isNaN(probability) || probability < 0 || probability > 100) {
        newErrors.probability = 'Probability must be between 0% and 100%';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }

    // Final validation using schema validator
    const validationData = {
      id: editingExpense?.id || generateId(),
      description: formData.description.trim(),
      category: formData.category,
      amount: parseFloat(formData.amount),
      frequency: formData.frequency,
      startYear: parseInt(formData.startYear),
      endYear: parseInt(formData.endYear),
      inflationRate: parseFloat(formData.inflationRate) || 3.5,
      probability: parseFloat(formData.probability) || 100,
      isOneTime: formData.isOneTime,
      isEssential: formData.isEssential,
      priority: formData.priority,
      notes: formData.notes
    };

    const validation = validateExpense(validationData);
    
    if (!validation.isValid) {
      // Merge any additional validation errors
      validation.errors.forEach(error => {
        if (error.includes('description') && !newErrors.description) newErrors.description = error;
        if (error.includes('amount') && !newErrors.amount) newErrors.amount = error;
        if (error.includes('category') && !newErrors.category) newErrors.category = error;
        if (error.includes('frequency') && !newErrors.frequency) newErrors.frequency = error;
        if (error.includes('startYear') && !newErrors.startYear) newErrors.startYear = error;
        if (error.includes('endYear') && !newErrors.endYear) newErrors.endYear = error;
        if (error.includes('inflationRate') && !newErrors.inflationRate) newErrors.inflationRate = error;
        if (error.includes('probability') && !newErrors.probability) newErrors.probability = error;
      });
      setErrors(newErrors);
      return false;
    }

    setErrors({});
    return true;
  };

  const handleSave = () => {
    if (validateForm()) {
      const expenseData = {
        id: editingExpense?.id || generateId(),
        description: formData.description.trim(),
        category: formData.category,
        amount: parseFloat(formData.amount),
        frequency: formData.frequency,
        startYear: parseInt(formData.startYear),
        endYear: parseInt(formData.endYear),
        inflationRate: parseFloat(formData.inflationRate),
        probability: parseInt(formData.probability),
        isOneTime: formData.frequency === 'one-time' || formData.isOneTime,
        isEssential: formData.isEssential,
        priority: formData.priority,
        notes: formData.notes.trim()
      };
      onSave(expenseData);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-1200 p-4 overflow-y-auto">
      <div className="bg-card rounded-lg shadow-floating w-full max-w-2xl min-h-fit my-4 mx-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-semibold text-foreground">
            {editingExpense ? 'Edit Expense' : 'Add New Expense'}
          </h2>
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={onClose}
            className="flex-shrink-0"
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* Validation Error Summary */}
          {Object.keys(errors).length > 0 && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Icon name="AlertCircle" size={20} className="text-destructive flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-destructive mb-1">
                    Please fix the following errors:
                  </h3>
                  <ul className="text-sm text-destructive space-y-1">
                    {Object.entries(errors).map(([field, error]) => (
                      <li key={field} className="flex items-start gap-2">
                        <span className="text-destructive">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Expense Description"
                type="text"
                placeholder="e.g., Monthly groceries, Annual health checkup"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                error={errors.description}
                required
              />
            </div>

            <Input
              label="Amount (₹)"
              type="number"
              placeholder="5000"
              value={formData.amount}
              onChange={(e) => handleInputChange('amount', e.target.value)}
              error={errors.amount}
              description="Enter amount in Indian Rupees"
              required
            />

            <Select
              label="Category"
              options={categoryOptions}
              value={formData.category}
              onChange={(value) => handleInputChange('category', value)}
              error={errors.category}
              required
            />

            <Select
              label="Frequency"
              options={frequencyOptions}
              value={formData.frequency}
              onChange={(value) => handleInputChange('frequency', value)}
              error={errors.frequency}
              required
            />

            <Input
              label="Probability (%)"
              type="number"
              placeholder="100"
              min="1"
              max="100"
              value={formData.probability}
              onChange={(e) => handleInputChange('probability', e.target.value)}
              error={errors.probability}
            />

            <Input
              label="Start Year"
              type="number"
              min={currentYear - 1}
              max={maxEndYear}
              value={formData.startYear}
              onChange={(e) => handleInputChange('startYear', e.target.value)}
              error={errors.startYear}
              required
            />

            <Input
              label="End Year"
              type="number"
              min={currentYear - 1}
              max={maxEndYear}
              value={formData.endYear}
              onChange={(e) => handleInputChange('endYear', e.target.value)}
              error={errors.endYear}
              required
            />

            <Input
              label="Inflation Rate (%)"
              type="number"
              step="0.1"
              placeholder="3.5"
              value={formData.inflationRate}
              onChange={(e) => handleInputChange('inflationRate', e.target.value)}
              error={errors.inflationRate}
            />
          </div>

          <div className="bg-muted p-4 rounded-lg">
            <h3 className="font-medium text-foreground mb-2">Expense Preview</h3>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>Annual Cost: {formatCurrencyINR(parseFloat(formData.amount || 0) * (formData.frequency === 'monthly' ? 12 : formData.frequency === 'quarterly' ? 4 : 1))}</p>
              <p>Duration: {formData.endYear - formData.startYear + 1} years</p>
              <p>Total Projected Cost: {formatCurrencyINR((parseFloat(formData.amount || 0) * (formData.frequency === 'monthly' ? 12 : formData.frequency === 'quarterly' ? 4 : 1)) * (formData.endYear - formData.startYear + 1))}</p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!effectiveCanWrite} title={isDemoModeActive ? 'Cannot save in demo mode' : 'Save'}>
            {editingExpense ? 'Update Expense' : 'Add Expense'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal;